import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, FileText, Receipt, Eye, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, inr, fmtDate, today } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, SearchBox, TableWrap, Empty, th, td, tr, statusLabel } from "@/components/common";

const TABS = ["All", "Created", "Loading", "In-Transit", "Delivered", "Bill Pending", "Bill Created", "Unbilled"];
const MON = (d) => (d ? ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][+d.slice(5, 7) - 1] : "");
const done = (b) => b.status === "Delivered" || (!!b.market_trip_id && b.status === "In-Transit");
export const tripDate = (b) => {
  const h = Object.fromEntries((b.status_history || []).map((x) => [x.status, x.at]));
  return ((b.market_trip_id ? h["In-Transit"] : "") || h.Delivered || b.delivered_at || b.booking_date || "").slice(0, 10);
};
const curMonth = () => new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 7);
const unbilledPast = (b) => done(b) && !b.bill_created_at && tripDate(b).slice(0, 7) < curMonth();
export const auditTag = (b) => {
  const tm = tripDate(b).slice(0, 7), bm = (b.bill_created_at || "").slice(0, 7);
  if (bm && tm && bm !== tm) return `Completed in ${MON(tm)} - Billed in ${MON(bm)}`;
  if (unbilledPast(b)) return `Completed in ${MON(tm)} - Unbilled`;
  return "";
};
const inTab = (b, t) => t === "All" || (t === "Unbilled" ? unbilledPast(b) : t === "Bill Pending" ? done(b) && !b.bill_created_at : t === "Bill Created" ? !!b.bill_created_at : b.status === t);
const stLabel = (b) => (b.bill_created_at ? "Bill Created" : b.status === "Delivered" ? "Loading Point (Bill Pending)" : statusLabel(b.status));
const DL_STATUS = [["All", "All"], ["In-Transit", "Delivery"], ["Delivered", "Loading Point"], ["Bill Pending", "Bill Pending"], ["Bill Created", "Bill Created"]];

export default function Bookings() {
  const { role } = useAuth();
  const hideFreight = role === "operation";
  const canPnl = role === "admin" || role === "accounts";
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("All");
  const [from, setFrom] = useState("");
  const [dlOpen, setDlOpen] = useState(false);
  const [dlType, setDlType] = useState("monthly");
  const [dlMonth, setDlMonth] = useState(today().slice(0, 7));
  const [dlVehicle, setDlVehicle] = useState("");
  const [dlStatus, setDlStatus] = useState("All");
  const navigate = useNavigate();
  useEffect(() => { api.get("/bookings").then((r) => setItems(r.data)); }, []);
  const vehicles = useMemo(() => {
    const m = {};
    for (const b of items) if (b.vehicle_id) m[b.vehicle_id] = b.vehicle_number || b.vehicle_id;
    return Object.entries(m);
  }, [items]);

  const downloadReport = async () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const save = (lines, name) => {
      const blob = new Blob(["﻿" + lines.map((r) => r.map(esc).join(",")).join("\n")], { type: "text/csv;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = name;
      a.click();
      URL.revokeObjectURL(a.href);
      setDlOpen(false);
    };
    if (dlType === "pnl") {
      const vehNum = vehicles.find(([id]) => id === dlVehicle)?.[1];
      try {
        const { data } = await api.get("/finance/pnl");
        const list = data.rows.filter((r) => (r.booking_date || "").startsWith(dlMonth) && r.vehicle_number === vehNum);
        if (!list.length) { toast.error("Is vehicle ka is month me koi trip nahi mila"); return; }
        const header = ["LR No", "Date", "Route", "Customer", "Freight (Rs)", "Fuel", "Toll", "Salary & Fooding", "Other Expenses", "Total Expenses", "Driver Salary+KM", "Market Vehicle Hire", "Net Profit", "Status"];
        const lines = [header];
        const tot = { f: 0, fuel: 0, toll: 0, allow: 0, other: 0, exp: 0, dc: 0, mc: 0, p: 0 };
        for (const r of list) {
          const eb = r.expense_breakdown || {};
          const other = Math.round(((r.expense_total || 0) - (eb.fuel || 0) - (eb.toll || 0) - (eb.allowance || 0)) * 100) / 100;
          tot.f += r.freight_amount || 0; tot.fuel += eb.fuel || 0; tot.toll += eb.toll || 0; tot.allow += eb.allowance || 0;
          tot.other += other; tot.exp += r.expense_total || 0; tot.dc += r.driver_cost || 0; tot.mc += r.market_cost || 0; tot.p += r.profit || 0;
          lines.push([r.lr_number, fmtDate(r.booking_date), `${r.pickup_location} -> ${r.drop_location}`, r.customer_name,
            r.freight_amount || 0, eb.fuel || 0, eb.toll || 0, eb.allowance || 0, other, r.expense_total || 0, r.driver_cost || 0, r.market_cost || 0, r.profit || 0, r.status]);
        }
        lines.push(["TOTAL", "", "", "", tot.f.toFixed(2), tot.fuel.toFixed(2), tot.toll.toFixed(2), tot.allow.toFixed(2), tot.other.toFixed(2), tot.exp.toFixed(2), tot.dc.toFixed(2), tot.mc.toFixed(2), tot.p.toFixed(2), ""]);
        save(lines, `Vehicle_PnL_${vehNum}_${dlMonth}.csv`);
      } catch (e) { toast.error("Report load nahi hui"); }
      return;
    }
    const list = (dlType === "monthly"
      ? items.filter((b) => (b.booking_date || "").startsWith(dlMonth))
      : items.filter((b) => b.vehicle_id === dlVehicle)).filter((b) => inTab(b, dlStatus));
    if (!list.length) { toast.error("Is selection me koi booking nahi mili"); return; }
    const cols = ["LR No", "Date", "Invoice No", "Route", "Consignor", "Consignee", "Vehicle", "Driver", "Material", "Weight (MT)", ...(hideFreight ? [] : ["Freight (Rs)", "Total Bill (Rs)"]), "Status", "Bill Date"];
    const lines = [cols];
    let freightSum = 0, billSum = 0;
    for (const b of list) {
      freightSum += b.freight_amount || 0;
      billSum += b.total_bill || 0;
      lines.push([b.lr_number, fmtDate(b.booking_date), b.invoice_number || "", `${b.pickup_location} -> ${b.drop_location}`, b.consignor?.name || "", b.consignee?.name || "",
        b.vehicle_number || "Unassigned", b.driver_name || "-", b.material_type, b.weight_tons || 0,
        ...(hideFreight ? [] : [b.freight_amount || 0, b.total_bill || 0]), stLabel(b), b.bill_created_at ? fmtDate(b.bill_created_at) : ""]);
    }
    if (!hideFreight) lines.push(["TOTAL", "", "", "", "", "", "", "", "", "", freightSum.toFixed(2), billSum.toFixed(2), "", ""]);
    const vehName = dlType === "vehicle" ? (vehicles.find(([id]) => id === dlVehicle)?.[1] || "vehicle") : dlMonth;
    save(lines, `Bookings_${dlType === "monthly" ? "Monthly" : "Vehicle"}_${vehName}${dlStatus === "All" ? "" : `_${DL_STATUS.find(([k]) => k === dlStatus)[1].replace(/ /g, "")}`}.csv`);
  };
  const rows = useMemo(() => items.filter((b) =>
    inTab(b, tab) && (!from || b.booking_date >= from) &&
    `${b.lr_number} ${b.consignor.name} ${b.consignee.name} ${b.pickup_location} ${b.drop_location} ${b.vehicle_number || ""} ${b.driver_name || ""} ${b.material_type}`.toLowerCase().includes(q.toLowerCase())
  ), [items, q, tab, from]);

  return (
    <div>
      <PageHeader title="Manual LR / Bilty" subtitle="Physical paper LRs, dispatch and trip progress">
        <Button variant="outline" data-testid="report-download-btn" onClick={() => setDlOpen(true)}><Download className="mr-1 h-4 w-4" />Download Report</Button>
        <Button data-testid="new-booking-btn" onClick={() => navigate("/bookings/new")}><Plus className="mr-1 h-4 w-4" />New Manual LR</Button>
      </PageHeader>
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-1 rounded-lg border bg-card p-1">
          {TABS.map((t) => (
            <button key={t} data-testid={`booking-tab-${t}`} onClick={() => setTab(t)}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${tab === t ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {t === "Unbilled" ? "Unbilled (pichle mahine)" : statusLabel(t)} <span className="ml-1 opacity-70">{items.filter((b) => inTab(b, t)).length}</span>
            </button>
          ))}
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <input data-testid="booking-from-date" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="native-select sm:w-40" title="From date" />
          <SearchBox testid="booking-search-input" value={q} onChange={setQ} placeholder="Search LR, party, city, vehicle…" />
        </div>
      </div>
      <TableWrap>
        <thead><tr>{["LR No", "Date", "Route", "Consignor → Consignee", "Vehicle / Driver", ...(hideFreight ? [] : ["Freight"]), "Status", ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((b) => (
            <tr key={b.id} className={`${tr} cursor-pointer`} onClick={() => navigate(`/bookings/${b.id}`)} data-testid={`booking-row-${b.lr_number}`}>
              <td className={`${td} font-mono font-semibold text-primary`}>{b.lr_number}{b.party_booking_no && <div className="text-[10px] font-normal text-muted-foreground">{b.party_booking_no}</div>}</td>
              <td className={td}>{fmtDate(b.booking_date)}</td>
              <td className={td}><div className="font-medium">{b.pickup_location} → {b.drop_location}</div><div className="text-xs text-muted-foreground">{b.material_type} · {b.weight_tons} T</div></td>
              <td className={td}><div className="max-w-[220px] truncate">{b.consignor.name}</div><div className="max-w-[220px] truncate text-xs text-muted-foreground">{b.consignee.name}</div></td>
              <td className={td}>{b.vehicle_number ? <><div className="font-mono text-xs font-semibold">{b.vehicle_number}</div><div className="text-xs text-muted-foreground">{b.driver_name || "-"}</div></> : <span className="text-xs text-amber-600 font-semibold">Unassigned</span>}</td>
              {!hideFreight && <td className={`${td} amount`}>{b.freight_amount ? inr(b.freight_amount) : <span data-testid={`freight-pending-${b.lr_number}`} className="text-xs font-semibold text-amber-600">Freight Pending</span>}</td>}
              <td className={td}><StatusBadge status={b.status} testid={`booking-status-${b.lr_number}`} />
                {auditTag(b) && <div data-testid={`booking-audit-${b.lr_number}`} className={`mt-1 inline-block rounded border px-1.5 py-0.5 text-[10px] font-bold ${b.bill_created_at ? "border-sky-300 bg-sky-50 text-sky-800" : "border-rose-300 bg-rose-50 text-rose-700"}`}>[{auditTag(b)}]</div>}</td>
              <td className={`${td} text-right`} onClick={(e) => e.stopPropagation()}>
                <Link to={`/bookings/${b.id}`} data-testid={`view-booking-${b.lr_number}`} className="inline-flex rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground" title="View"><Eye className="h-4 w-4" /></Link>
                <Link to={`/print/lr/${b.id}`} data-testid={`print-lr-${b.lr_number}`} className="inline-flex rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground" title="Lorry Receipt"><FileText className="h-4 w-4" /></Link>
                <Link to={`/print/invoice/${b.id}`} data-testid={`print-invoice-${b.lr_number}`} className="inline-flex rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground" title="Invoice"><Receipt className="h-4 w-4" /></Link>
              </td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      {rows.length === 0 && <Empty testid="bookings-empty" />}
      <Dialog open={dlOpen} onOpenChange={setDlOpen}>
        <DialogContent data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle>Download Booking Report</DialogTitle>
            <DialogDescription>Excel/CSV sheet — monthly ya vehicle-wise. Jo chuna hai wahi records download honge.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className={`grid gap-2 ${canPnl ? "grid-cols-3" : "grid-cols-2"}`} data-testid="report-type-toggle">
              {[["monthly", "Monthly Report"], ["vehicle", "Vehicle-wise Report"], ...(canPnl ? [["pnl", "Vehicle P&L (Monthly)"]] : [])].map(([v, l]) => (
                <button key={v} type="button" data-testid={`report-type-${v}`} onClick={() => setDlType(v)}
                  className={`min-h-[40px] rounded-lg border px-2 text-xs font-semibold transition-colors ${dlType === v ? "border-primary bg-primary/10 text-primary" : "text-muted-foreground hover:border-ring"}`}>{l}</button>
              ))}
            </div>
            {(dlType === "monthly" || dlType === "pnl") && (
              <div>
                <div className="label-xs mb-1">Month</div>
                <input data-testid="report-month-input" type="month" value={dlMonth} onChange={(e) => setDlMonth(e.target.value)} className="native-select w-full" />
              </div>
            )}
            {(dlType === "vehicle" || dlType === "pnl") && (
              <div>
                <div className="label-xs mb-1">Vehicle</div>
                <select data-testid="report-vehicle-select" className="native-select" value={dlVehicle} onChange={(e) => setDlVehicle(e.target.value)}>
                  <option value="">Select vehicle…</option>
                  {vehicles.map(([id, num]) => <option key={id} value={id}>{num}</option>)}
                </select>
              </div>
            )}
            {dlType !== "pnl" && (
              <div>
                <div className="label-xs mb-1">Trip Status</div>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-5" data-testid="report-status-toggle">
                  {DL_STATUS.map(([k, l]) => (
                    <button key={k} type="button" data-testid={`report-status-${k.replace(/ /g, "-")}`} onClick={() => setDlStatus(k)}
                      className={`min-h-[36px] rounded-lg border px-2 text-xs font-semibold transition-colors ${dlStatus === k ? "border-primary bg-primary/10 text-primary" : "text-muted-foreground hover:border-ring"}`}>{l}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlOpen(false)}>Cancel</Button>
            <Button data-testid="report-download-confirm-btn" onClick={downloadReport} disabled={(dlType === "vehicle" || dlType === "pnl") && !dlVehicle}>
              <Download className="mr-1 h-4 w-4" /> Download Sheet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
